import { Component } from '@theme/component';
import { hydrateDeferredImages } from '@theme/deferred-image';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];
  #shouldFocusSearchOnOpen = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('click', this.#onClick);
    document.addEventListener('click', this.#onMobileSearchButtonClick, true);
    this.#setupAnimatedElementListeners();
    this.#setInitialTabState();
    this.#syncSearchResetButtons();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('click', this.#onMobileSearchButtonClick, true);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Implements the WAI-ARIA keyboard pattern for the top-level tab list.
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    const tabButton = event.target instanceof Element ? event.target.closest('[data-menu-drawer-tab]') : null;
    if (!(tabButton instanceof HTMLButtonElement)) return;

    const tabButtons = [...this.querySelectorAll('[data-menu-drawer-tab]')].filter(
      (button) => button instanceof HTMLButtonElement
    );
    const currentIndex = tabButtons.indexOf(tabButton);
    if (currentIndex < 0) return;

    let nextIndex;
    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % tabButtons.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + tabButtons.length) % tabButtons.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabButtons.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextTab = tabButtons[nextIndex];
    this.#selectTab(nextTab?.dataset.menuDrawerTab);
    nextTab?.focus();
  };

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const tabButton = event.target instanceof Element ? event.target.closest('[data-menu-drawer-tab]') : null;
    if (!(tabButton instanceof HTMLButtonElement)) return;

    this.#selectTab(tabButton.dataset.menuDrawerTab);
  };

  /**
   * On mobile, route header search button click to open menu drawer.
   * @param {MouseEvent} event
   */
  #onMobileSearchButtonClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    const searchButton = event.target.closest('[data-mobile-open-menu-drawer]');
    if (!(searchButton instanceof HTMLButtonElement)) return;

    if (this.isOpen) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const summary = this.refs.details.querySelector('summary');
    if (summary instanceof HTMLElement) {
      this.#shouldFocusSearchOnOpen = true;
      summary.click();
    }
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    hydrateDeferredImages(this.refs.menuDrawer).catch(console.error);

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      } else {
        this.#resetTabs();
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(
        drawer || details,
        () => {
          trapFocus(details);
          if (this.#shouldFocusSearchOnOpen && details === this.refs.details) {
            this.#focusDrawerSearchInput();
          }
          this.#shouldFocusSearchOnOpen = false;
        },
        { subtree: false }
      );
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    this.#shouldFocusSearchOnOpen = false;
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  #setInitialTabState() {
    const activeTabButton =
      this.querySelector('[data-menu-drawer-tab][data-selected]') ?? this.querySelector('[data-menu-drawer-tab]');

    if (!(activeTabButton instanceof HTMLElement)) return;

    this.#selectTab(activeTabButton.dataset.menuDrawerTab);
  }

  /**
   * Toggle reset button visibility when search input value changes.
   * @param {Event} event
   */
  toggleSearchReset(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!event.target.classList.contains('menu-drawer__search-input')) return;
    this.#setSearchResetVisibility(event.target);
  }

  /**
   * Clears drawer search input and updates reset button state.
   * @param {Event} event
   */
  resetSearch(event) {
    event.preventDefault();
    if (!(event.target instanceof Element)) return;

    const form = event.target.closest('.menu-drawer__search-form');
    const input = form?.querySelector('.menu-drawer__search-input');
    if (!(input instanceof HTMLInputElement)) return;

    input.value = '';
    this.#setSearchResetVisibility(input);
    input.focus();
  }

  #resetTabs() {
    this.#setInitialTabState();
  }

  /**
   * @param {string | undefined} tabId
   */
  #selectTab(tabId) {
    if (!tabId) return;

    const tabButtons = this.querySelectorAll('[data-menu-drawer-tab]');
    const tabPanels = this.querySelectorAll('[data-menu-drawer-panel]');

    tabButtons.forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      const isActive = button.dataset.menuDrawerTab === tabId;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', String(isActive));
      button.tabIndex = isActive ? 0 : -1;

      if (isActive) {
        button.setAttribute('data-selected', '');
      } else {
        button.removeAttribute('data-selected');
      }
    });

    tabPanels.forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      const isActive = panel.dataset.menuDrawerPanel === tabId;
      panel.classList.toggle('is-active', isActive);
      panel.toggleAttribute('hidden', !isActive);
      panel.setAttribute('aria-hidden', String(!isActive));
    });
  }

  #syncSearchResetButtons() {
    const searchInputs = this.querySelectorAll('.menu-drawer__search-input');
    searchInputs.forEach((input) => {
      if (input instanceof HTMLInputElement) {
        this.#setSearchResetVisibility(input);
      }
    });
  }

  /**
   * Focuses the drawer search input after the menu is opened.
   */
  #focusDrawerSearchInput() {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const searchInput = this.querySelector('.menu-drawer__search-input');
        if (searchInput instanceof HTMLInputElement) {
          searchInput.focus({ preventScroll: true });
        }
      });
    });
  }

  /**
   * @param {HTMLInputElement} input
   */
  #setSearchResetVisibility(input) {
    const form = input.closest('.menu-drawer__search-form');
    const resetButton = form?.querySelector('.menu-drawer__search-reset-button');
    if (!(resetButton instanceof HTMLButtonElement)) return;

    const hasText = input.value.trim().length > 0;
    resetButton.hidden = !hasText;
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
